/**
 * What an audit entry changed, field by field (knowledge doc section 11: "audit
 * log with before/after comparison"; P8.3).
 *
 * The same reading as the API's `diffSettings`, which the settings history uses:
 * dotted paths, and a list is one value rather than a sequence of insertions,
 * because the screens edit lists whole. Here rather than shared because the API's
 * walks canonical JSON built for hashing, which a display has no need of; this one
 * only has to tell equal from different.
 */

export interface FieldChange {
  /** `''` when the whole value is not an object (a list, a string). */
  path: string;
  kind: 'added' | 'removed' | 'changed';
  before: unknown;
  after: unknown;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** Equal as JSON, whatever order an object's keys came in. */
const sameValue = (left: unknown, right: unknown): boolean => {
  if (isPlainObject(left) && isPlainObject(right)) {
    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    return [...keys].every((key) => sameValue(left[key], right[key]));
  }
  if (Array.isArray(left) && Array.isArray(right)) {
    return left.length === right.length && left.every((item, i) => sameValue(item, right[i]));
  }
  return (left ?? null) === (right ?? null);
};

export function diffFields(before: unknown, after: unknown): FieldChange[] {
  const changes: FieldChange[] = [];
  const walk = (path: string, left: unknown, right: unknown): void => {
    if (isPlainObject(left) && isPlainObject(right)) {
      for (const key of [...new Set([...Object.keys(left), ...Object.keys(right)])].sort()) {
        walk(path === '' ? key : `${path}.${key}`, left[key], right[key]);
      }
      return;
    }
    if (sameValue(left, right)) return;
    const absent = (value: unknown): boolean => value === undefined || value === null;
    changes.push({
      path,
      kind: absent(left) ? 'added' : absent(right) ? 'removed' : 'changed',
      before: left,
      after: right,
    });
  };
  // An entry with only `after` (something created) is every field of it, added.
  walk(
    '',
    before ?? (isPlainObject(after) ? {} : null),
    after ?? (isPlainObject(before) ? {} : null),
  );
  return changes;
}

/** A value as a person reads it in one line. */
export function describeValue(value: unknown): string {
  if (value === undefined || value === null) return 'nothing';
  if (Array.isArray(value)) {
    return value.length === 0 ? 'nothing' : value.map((item) => describeValue(item)).join(', ');
  }
  if (typeof value === 'boolean') return value ? 'on' : 'off';
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}
